/**
 * Poll drafting rules for the web composers. Mirrors `backend/src/graphql/utils/poll.ts` — the
 * server re-checks everything, this is what lets the composer say so before submitting.
 */

export const POLL = 'POLL';
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 10;
export const POLL_OPTION_MAX_LENGTH = 80;

export type PollDraft = {
    options: string[];
    allowMultiple: boolean;
    allowOther: boolean;
    /** A `datetime-local` value, or '' for never. */
    closesAt: string;
};

export const emptyPollDraft = (): PollDraft => ({
    options: ['', ''],
    allowMultiple: false,
    allowOther: false,
    closesAt: '',
});

const filledOptions = (draft: PollDraft) => draft.options.map((o) => o.trim()).filter(Boolean);

/** Why this draft can't be posted yet, or null when it can. */
export const pollDraftProblem = (draft: PollDraft, now: Date = new Date()): string | null => {
    const options = filledOptions(draft);
    if (options.length < POLL_MIN_OPTIONS) return `Add at least ${POLL_MIN_OPTIONS} answers.`;
    if (options.length > POLL_MAX_OPTIONS) return `A poll can have at most ${POLL_MAX_OPTIONS} answers.`;
    if (options.some((o) => o.length > POLL_OPTION_MAX_LENGTH)) {
        return `Each answer must be ${POLL_OPTION_MAX_LENGTH} characters or fewer.`;
    }
    const lower = options.map((o) => o.toLowerCase());
    if (new Set(lower).size !== lower.length) return 'Two answers are the same.';
    if (draft.closesAt) {
        const closes = new Date(draft.closesAt);
        if (Number.isNaN(closes.getTime())) return 'That closing time is not a valid date.';
        if (closes <= now) return 'The closing time has to be in the future.';
    }
    return null;
};

/** The poll arguments `createPost` / `createLeadershipPost` take. */
export const pollVariables = (draft: PollDraft) => ({
    pollOptions: filledOptions(draft),
    pollAllowMultiple: draft.allowMultiple,
    pollAllowOther: draft.allowOther,
    pollClosesAt: draft.closesAt ? new Date(draft.closesAt).toISOString() : null,
});

export const POLL_STATUS_LABEL: Record<string, { text: string; color: 'green' | 'grey' | 'yellow' | 'blue' | 'red' | 'orange' }> = {
    LIVE: { text: 'Live', color: 'green' },
    CLOSED: { text: 'Closed', color: 'grey' },
    AWAITING_REVIEW: { text: 'Awaiting review', color: 'yellow' },
    SCHEDULED: { text: 'Scheduled', color: 'blue' },
    TAKEN_DOWN: { text: 'Taken down', color: 'orange' },
    REJECTED: { text: 'Rejected', color: 'red' },
};

type CsvRespondent = {
    name?: string | null;
    organisationName?: string | null;
    answers: string[];
    otherText?: string | null;
    respondedAt?: string | null;
};

/** Every respondent as CSV — one row each, answers joined with "; ". */
export const pollResultsCsv = (question: string, respondents: CsvRespondent[]): string => {
    const cell = (value: string | null | undefined) => {
        const text = value ?? '';
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const rows = [
        ['Question', question],
        [],
        ['Name', 'Organisation', 'Answers', 'Something else', 'Answered at'],
        ...respondents.map((r) => [r.name ?? '', r.organisationName ?? '', r.answers.join('; '), r.otherText ?? '', r.respondedAt ?? '']),
    ];
    return rows.map((row) => row.map(cell).join(',')).join('\n');
};
